import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { doctorOffersService, effectiveBookingPrice, normalizeBookingService, normalizeDoctorServiceAssignment } from "../lib/bookingOptions";
import { AppointmentSlotPicker } from "./AppointmentSlotPicker";

export function FollowupConfirmModal({ followupId, onClose, onConfirmed }) {
  const [form, setForm] = useState({ channel: "phone", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/clinic/followups-x/${followupId}/confirm`, form);
      toast.success("Patient confirmation recorded");
      onConfirmed();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to record confirmation");
    } finally {
      setSubmitting(false);
    }
  };
  return <div className="fixed inset-0 bg-black/50 z-[80] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Confirm patient agreement">
    <div className="bg-white rounded-2xl p-6 max-w-lg w-full">
      <div className="flex justify-between items-center mb-4"><h3 className="font-bold text-mg-navy text-lg">Confirm patient agreement</h3><button type="button" onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button></div>
      <p className="text-sm text-slate-600 mb-3">Only confirm after the patient agrees to book the follow-up appointment.</p>
      <label className="text-xs text-slate-500 block mb-3">Confirmation channel
        <select className="mg-input w-full mt-1" value={form.channel} onChange={(event) => setForm({ ...form, channel: event.target.value })}>
          <option value="phone">Phone</option><option value="sms">SMS</option><option value="whatsapp">WhatsApp</option><option value="email">Email</option><option value="in_person">In person</option>
        </select>
      </label>
      <label className="text-xs text-slate-500 block">Confirmation notes
        <textarea className="mg-input w-full mt-1" rows={3} value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} />
      </label>
      <div className="flex gap-2 mt-5"><button type="button" onClick={onClose} className="mg-btn-secondary flex-1">Cancel</button><button type="button" onClick={submit} disabled={submitting} className="mg-btn-primary flex-1">{submitting ? "Saving…" : "Confirm and choose slot"}</button></div>
    </div>
  </div>;
}

export function FollowupConvertModal({ followupId, doctorId, clinicId, initialDate, onClose, onConverted }) {
  const [services, setServices] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [form, setForm] = useState({ service_id: "", selectedSlot: null, notes: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Receptionists do not need the clinic service-management permission to
    // book a confirmed follow-up. The visit-options endpoint is the shared,
    // role-safe source used by the front desk and includes doctor assignments.
    api.get("/visits/options")
      .then((response) => {
        const rows = (response.data?.services || []).map(normalizeBookingService);
        const assignments = (response.data?.doctor_services || []).map(normalizeDoctorServiceAssignment);
        setAssignments(assignments);
        setServices(rows.filter((service) => service.service_id
          && service.status !== "inactive" && service.status !== "deleted"
          && doctorOffersService(assignments, doctorId, service.service_id)));
      })
      .catch(() => toast.error("Assigned services could not be loaded"));
  }, [doctorId]);

  const submit = async () => {
    if (!form.service_id || !form.selectedSlot) return toast.error("Pick a service and available slot");
    setSubmitting(true);
    try {
      const response = await api.post(`/clinic/followups-x/${followupId}/convert`, {
        service_id: form.service_id,
        appointment_datetime: form.selectedSlot.start,
        notes: form.notes || null,
      });
      toast.success(`Appointment created (${response.data.duration_minutes} min)`);
      onConverted();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to convert follow-up");
    } finally {
      setSubmitting(false);
    }
  };

  return <div className="fixed inset-0 bg-black/50 z-[80] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Convert follow-up to appointment">
    <div className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-bold text-mg-navy text-lg">Convert to appointment</h3>
        <button type="button" onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
      </div>
      <div className="space-y-3">
        <div>
          <label className="text-xs text-slate-500 mb-1 block">Service *</label>
          <select className="mg-input w-full" value={form.service_id} onChange={(event) => setForm({ ...form, service_id: event.target.value, selectedSlot: null })} data-testid="convert-service">
            <option value="">— pick an assigned service —</option>
            {services.map((service) => <option key={service.service_id} value={service.service_id}>{service.name} · {service.duration_minutes}min · ₹{effectiveBookingPrice(services, assignments, doctorId, service.service_id)}</option>)}
          </select>
          {!services.length && <p className="text-xs text-slate-500 mt-1">No active services are assigned to this doctor.</p>}
        </div>
        {form.service_id && <AppointmentSlotPicker clinicId={clinicId} doctorId={doctorId} serviceId={form.service_id} initialDate={initialDate} onSelect={(selectedSlot) => setForm({ ...form, selectedSlot })} />}
        {form.selectedSlot && <p className="text-sm font-semibold text-emerald-700">Selected: {form.selectedSlot.local_start?.replace("T", " ").slice(0, 16)} ({form.selectedSlot.timezone})</p>}
        <textarea className="mg-input w-full" rows={2} placeholder="Notes (optional)" value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} data-testid="convert-notes" />
      </div>
      <div className="flex gap-2 mt-5">
        <button type="button" onClick={onClose} className="mg-btn-secondary flex-1 justify-center">Cancel</button>
        <button type="button" onClick={submit} disabled={submitting || !form.selectedSlot} className="mg-btn-primary flex-1 justify-center" data-testid="convert-submit">
          {submitting ? "Booking…" : "Book appointment"}
        </button>
      </div>
    </div>
  </div>;
}
